<template>
  <slot v-if="state.error && $slots.fallback" name="fallback" />
  <nuxt-img v-else v-bind="bind" @error="onError" @load="onLoad" />
</template>

<script lang="ts" setup>
const properties = withDefaults(
  defineProps<{
    src: string;
    alt: string;
    width?: number;
    height?: number;
    loading?: "lazy" | "eager";
    provider?: string;
  }>(),
  {
    loading: "lazy",
    provider: undefined,
    width: undefined,
    height: undefined,
  },
);

const {
  public: {
    cloudinary: { baseUrl },
  },
} = useRuntimeConfig();

const resolvedProvider = computed<"cloudinary" | undefined>(() =>
  properties.src.includes(baseUrl) && !properties.src.endsWith(".svg")
    ? "cloudinary"
    : undefined,
);

const bind = computed(() => {
  const src =
    resolvedProvider.value === "cloudinary"
      ? properties.src.replace(baseUrl, "")
      : properties.src;

  return {
    ...properties,
    width: properties.width,
    height: properties.height,
    provider: resolvedProvider.value,
    src,
    /*
    Forcing srcset to null for:
    - we don't need different format for vector images
    - Cloudinary images as width superior to native image width leads to loading error
     */
    ...(resolvedProvider.value === "cloudinary" || src.endsWith(".svg")
      ? { srcset: undefined }
      : {}),
    style: {
      "--imageHeight": properties.height ? `${properties.height}px` : undefined,
    },
    class: {
      // Force image height to ensure it's repsected in case only height is provided
      "h-(--imageHeight)":
        properties.height !== undefined && properties.width === undefined,
    },
    decoding: "auto" as "auto" | "async" | "sync" | undefined,
    densities: "x1 x2",
  };
});

const state = reactive({ loaded: false, error: false });

const onLoad = () => {
  state.loaded = true;
  state.error = false;
};

const onError = () => {
  state.loaded = false;
  state.error = true;
};
</script>
